import React, { memo, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components/macro';
import { Button } from 'core/components/Button';

interface Props {
  inputValidate?: boolean; // Indicates if the input is valid
  isDirty?: boolean; // Indicates if the form has been modified
}

const TooltipContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const CustomTooltip = styled.div<{ show: boolean; position: { top: number; left: number } }>`
  position: fixed;
  top: ${props => props.position.top}px;
  left: ${props => props.position.left}px;
  transform: translateX(-50%) translateY(-100%);
  margin-bottom: 8px;
  background-color: white;
  color: black;
  padding: 8px 12px;
  border-radius: 8px;
  box-shadow: 0px 4px 8px 2px rgba(0, 0, 0, 0.15);
  font-weight: 500;
  z-index: 999999;
  white-space: nowrap;
  opacity: ${props => props.show ? 1 : 0};
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;
  pointer-events: none;
  
  /* Arrow */
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: white;
  }
`;

export const SubmitButton = memo((props: Props) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const containerRef:any = useRef(null);

  // Determine if the button should be disabled
  const isButtonDisabled = !props.isDirty || props.inputValidate;

  const getMessage = () => {
    if (!!props.inputValidate) {
      return "Please fill out all fields to submit the form!";
    } else if (!props.isDirty && !props.inputValidate) {
      return "Make changes to enable submit";
    }
    return "";
  }

  const toolTipMessage = getMessage();

  const updateTooltipPosition = () => {
    if (containerRef.current) {
      const rect = containerRef?.current?.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top - 8, // 8px margin above the button
        left: rect.left + rect.width / 2
      });
    }
  };

  const handleMouseEnter = () => {
    if (isButtonDisabled && toolTipMessage) {
      updateTooltipPosition();
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  // Update tooltip position on scroll or resize
  useEffect(() => {
    const handleUpdate = () => {
      if (showTooltip) {
        updateTooltipPosition();
      }
    };

    window.addEventListener('scroll', handleUpdate);
    window.addEventListener('resize', handleUpdate);

    return () => {
      window.removeEventListener('scroll', handleUpdate);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [showTooltip]);

  return (
    <div className="mb-[2vh] mt-[3vh]">
      <TooltipContainer
        ref={containerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Button
          submit
          type="primary"
          label="Submit"
          width=""
          disabled={isButtonDisabled}
        />
        
        {isButtonDisabled && toolTipMessage && createPortal(
          <CustomTooltip show={showTooltip} position={tooltipPosition}>
            {toolTipMessage}
          </CustomTooltip>,
          document.body
        )}
      </TooltipContainer>
    </div>
  );
});

const Div = styled.div``;